import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, getDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { FaStar, FaRegStar, FaTrash, FaEnvelopeOpenText } from 'react-icons/fa';
import YapperModal from '../components/YapperModal';

function ContactMessagesView({ user }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isStaff, setIsStaff] = useState(false);
  const [sortOrder, setSortOrder] = useState('newest'); // newest | oldest
  const [starFilter, setStarFilter] = useState('all'); // all | starred | unstarred
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null, showCancel: false });

  const showConfirm = (title, message, onConfirmCallback, type = 'warning') => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: () => {
        setModal((m) => ({ ...m, isOpen: false }));
        if (onConfirmCallback) onConfirmCallback();
      },
      showCancel: true,
    });
  };

  const loadMessages = useCallback(async () => {
    try {
      const snap = await getDocs(
        query(collection(db, 'contactMessages'), orderBy('createdAt', sortOrder === 'newest' ? 'desc' : 'asc'))
      );
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Failed to load contact messages:', e);
    } finally {
      setLoading(false);
    }
  }, [sortOrder]);

  useEffect(() => {
    const checkStaff = async () => {
      try {
        const userId = localStorage.getItem('userId');
        const adminDoc = await getDoc(doc(db, 'admins', userId));
        if (adminDoc.exists()) {
          setIsStaff(true);
        } else {
          const workerDoc = await getDoc(doc(db, 'workers', userId));
          if (workerDoc.exists()) setIsStaff(true);
        }
      } catch (e) {
        console.error('Error checking staff status:', e);
      }
    };
    checkStaff();
  }, [user]);

  useEffect(() => {
    if (isStaff) loadMessages();
  }, [isStaff, loadMessages]);

  const toggleStar = async (msg) => {
    try {
      await updateDoc(doc(db, 'contactMessages', msg.id), { starred: !msg.starred });
      setMessages((list) => list.map((m) => (m.id === msg.id ? { ...m, starred: !m.starred } : m)));
    } catch (e) {
      console.error('Failed to star message:', e);
    }
  };

  const deleteMessage = (msg) => {
    showConfirm('Delete Message', `Delete the message from ${msg.email}? This cannot be undone.`, async () => {
      try {
        await deleteDoc(doc(db, 'contactMessages', msg.id));
        setMessages((list) => list.filter((m) => m.id !== msg.id));
      } catch (e) {
        console.error('Failed to delete message:', e);
      }
    }, 'danger');
  };

  const formatDate = (ts) => {
    if (!ts || !ts.toDate) return '';
    return ts.toDate().toLocaleString();
  };

  const visible = messages.filter((m) =>
    starFilter === 'all' ? true : starFilter === 'starred' ? m.starred : !m.starred
  );

  if (!loading && !isStaff) {
    return (
      <div style={{ maxWidth: 700, margin: '3rem auto', padding: '0 1rem', textAlign: 'center', color: '#fff' }}>
        <h2>Access denied</h2>
        <p>This page is only available to staff.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 820, margin: '2rem auto', padding: '0 1rem' }}>
      <div
        style={{
          background: 'var(--surface-card, #fff)',
          border: '1px solid var(--border-subtle, rgba(0,0,0,0.1))',
          borderRadius: 'var(--radius-lg, 16px)',
          boxShadow: 'var(--shadow-md)',
          padding: '1.5rem',
          color: 'var(--text-body)',
        }}
      >
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', color: 'var(--text-strong)' }}>
          <FaEnvelopeOpenText /> Contact Messages
        </h1>
        <p style={{ color: 'var(--text-muted)', marginBottom: '1.25rem' }}>
          Messages submitted through the Contact Us form.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.25rem' }}>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            style={{ padding: '0.45rem 0.7rem', borderRadius: 8, border: '1px solid var(--border-subtle)' }}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          <select
            value={starFilter}
            onChange={(e) => setStarFilter(e.target.value)}
            style={{ padding: '0.45rem 0.7rem', borderRadius: 8, border: '1px solid var(--border-subtle)' }}
          >
            <option value="all">All</option>
            <option value="starred">Starred only</option>
            <option value="unstarred">Not starred</option>
          </select>
        </div>

        {loading ? (
          <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
        ) : visible.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>No messages.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {visible.map((m) => (
              <div
                key={m.id}
                style={{
                  border: '1px solid var(--border-subtle, rgba(0,0,0,0.12))',
                  borderRadius: 12,
                  padding: '0.9rem 1rem',
                  background: m.starred ? 'rgba(255, 213, 74, 0.12)' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: 'var(--text-strong)', wordBreak: 'break-word' }}>{m.subject}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--brand-600, var(--brand-500))', wordBreak: 'break-word' }}>{m.email}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{formatDate(m.createdAt)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    <button
                      onClick={() => toggleStar(m)}
                      title={m.starred ? 'Unstar' : 'Star'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f1c40f', fontSize: '1.15rem' }}
                    >
                      {m.starred ? <FaStar /> : <FaRegStar />}
                    </button>
                    <button
                      onClick={() => deleteMessage(m)}
                      title="Delete"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontSize: '1.05rem' }}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
                <p style={{ marginTop: '0.6rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--text-body)' }}>{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <YapperModal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
        onCancel={modal.showCancel ? () => setModal((m) => ({ ...m, isOpen: false })) : null}
      />
    </div>
  );
}

export default ContactMessagesView;
